import axios from 'axios';
import {BASE_URL} from './dynamicCodes/hooks';
import {getSearchParams, QUERY_STATE_KEY} from './urlQuery';

/**
 * The app opens web pages with a single-use link-out token (`?h=`) rather than the person's id, first name and
 * answers in the query string, where Segment, Sentry and the Facebook pixel would read them (RET-3252).
 *
 * The inline script near the top of `index.html` moves `h` into sessionStorage and strips it from the URL before any
 * of those load. `LinkOutGate` then calls `redeemPendingLinkOutToken` once, before any route renders: Hypnos spends
 * the token and returns the person id (the checkout identity the URL `token` used to carry), the first name and the
 * display params. They live in sessionStorage for the rest of the tab, and in localStorage and a cookie for 24 hours so
 * a revisit in a restored or new tab still has them, so a refresh or an in-app navigation keeps working after the token
 * is spent, and the param hooks in `dynamicCodes/hooks.ts` fall back to them.
 */

/** Same key as the inline script in index.html. Change both or neither. */
export const LINK_OUT_TOKEN_STORAGE_KEY = 'rise.linkout.h';
/** The tab's redeem nonce, same key as index.html: Hypnos answers a repeat of the same token and nonce again. */
export const LINK_OUT_NONCE_STORAGE_KEY = 'rise.linkout.n';
const SESSION_STORAGE_KEY = 'rise.linkout.session';

export type LinkOutSession = {
  personId: string;
  firstName: string | null;
  params: Record<string, string>;
  /** Pages this visit has shown, so an iOS Safari reload of an evicted tab (a plain load, no referrer) still counts. */
  paths?: string[];
  /** `linkOutTokenKey` of the token this session was redeemed from, so a reopen of that same link is a revisit. */
  tokenKey?: string;
};

/**
 * A fingerprint of a link-out token (53-bit cyrb53), saved with the session instead of the token itself. Chrome on
 * iOS reopens a page from its history with the original URL, spent `h` included; a matching fingerprint says that
 * load is a revisit of this session's own link rather than a new link for someone else. Same as `tokenKey` in
 * index.html. Change both or neither.
 */
export const linkOutTokenKey = (token: string): string => {
  let a = 0xdeadbeef;
  let b = 0x41c6ce57;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    a = Math.imul(a ^ c, 2654435761);
    b = Math.imul(b ^ c, 1597334677);
  }
  a = Math.imul(a ^ (a >>> 16), 2246822507) ^ Math.imul(b ^ (b >>> 13), 3266489909);
  b = Math.imul(b ^ (b >>> 16), 2246822507) ^ Math.imul(a ^ (a >>> 13), 3266489909);
  return (4294967296 * (2097151 & b) + (a >>> 0)).toString(36);
};

declare global {
  interface Window {
    __riseLinkOutH?: string;
    __riseLinkOutN?: string;
    /** Set by index.html when this load's URL carried h. */
    __riseLinkOutArrived?: boolean;
    /** Set by index.html when this load's h was the saved session's own token. */
    __riseLinkOutRevisit?: boolean;
    __riseLinkOutSession?: LinkOutSession;
    /** The purchase marker's memory copy, for when sessionStorage is blocked but the cookie isn't. */
    __riseLinkOutPurchased?: boolean;
    __riseLinkOutRedeem?: Promise<RedeemResult>;
    __riseLinkOutRedeemStartedAt?: number;
  }
}

const stripTokenFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const h = params.get('h');
  if (h === null) {
    return null;
  }
  params.delete('h');
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
  return h;
};

/** Same key as index.html, which saves its early request's response here with the token it spent. */
const EARLY_RESULT_STORAGE_KEY = 'rise.linkout.redeemed';

/** Forget the pending token, its nonce and any response index.html saved for it. */
const clearPendingRedeem = () => {
  clearPendingToken();
  try {
    window.sessionStorage.removeItem(EARLY_RESULT_STORAGE_KEY);
  } catch {
    // nothing saved
  }
};

const clearPendingToken = () => {
  try {
    window.sessionStorage.removeItem(LINK_OUT_TOKEN_STORAGE_KEY);
    window.sessionStorage.removeItem(LINK_OUT_NONCE_STORAGE_KEY);
  } catch {
    // nothing stored
  }
  delete window.__riseLinkOutH;
  delete window.__riseLinkOutN;
};

/** Keep a token taken from the URL (no inline script ran) where `readPendingLinkOutToken` will find it again. */
const storePendingToken = (h: string) => {
  try {
    window.sessionStorage.setItem(LINK_OUT_TOKEN_STORAGE_KEY, h);
  } catch {
    window.__riseLinkOutH = h;
  }
};

/**
 * The nonce index.html saved with the pending token, or a new one saved now (a page reached without the inline
 * script). Saved before the request goes out, so a reload mid-request can repeat it.
 */
const pendingNonce = (): string => {
  try {
    const stored = window.sessionStorage.getItem(LINK_OUT_NONCE_STORAGE_KEY);
    if (stored) {
      return stored;
    }
  } catch {
    // Storage blocked: fall through to memory.
  }
  if (window.__riseLinkOutN) {
    return window.__riseLinkOutN;
  }
  const bytes = window.crypto.getRandomValues(new Uint8Array(32));
  const nonce = btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  try {
    window.sessionStorage.setItem(LINK_OUT_NONCE_STORAGE_KEY, nonce);
  } catch {
    window.__riseLinkOutN = nonce;
  }
  return nonce;
};

/** The token waiting to be redeemed, if the page was opened from the app and hasn't redeemed it yet. */
export const readPendingLinkOutToken = (): string | null => {
  // Repeats the inline script's strip for pages reached without it (local tooling, tests).
  const fromUrl = stripTokenFromUrl();
  if (fromUrl) {
    return fromUrl;
  }
  try {
    const stored = window.sessionStorage.getItem(LINK_OUT_TOKEN_STORAGE_KEY);
    if (stored) {
      return stored;
    }
  } catch {
    // Storage blocked: fall through to the in-memory copy the inline script may have left.
  }
  return window.__riseLinkOutH || null;
};

/**
 * How long a session outlives its tab. A tab Safari restores, a page reopened from history or a new tab starts with
 * empty sessionStorage, so the session is also kept in localStorage (this device only) and expires this long after the
 * last page that used it.
 */
export const LINK_OUT_SESSION_KEEP_MS = 24 * 60 * 60 * 1000;

type SavedSession = {session?: LinkOutSession; savedAt?: number};

/**
 * A second device copy of the saved session, for iOS browsers that come back with this site's sessionStorage and
 * localStorage both empty while its cookies (Segment's included) survive. Same contents and lifetime as the
 * localStorage copy, saved and forgotten with it; read only when localStorage has none. Host-only (no Domain); the
 * browser sends it with each request to this host, though Netlify never reads it.
 */
export const SESSION_COOKIE_NAME = 'rise_linkout';
/** Browsers drop a cookie over 4096 bytes; a session too big for one just isn't kept in a cookie. */
const MAX_COOKIE_VALUE_LENGTH = 3800;

const readSessionCookie = (): SavedSession | null => {
  try {
    const cookie = document.cookie.split('; ').find(c => c.startsWith(`${SESSION_COOKIE_NAME}=`));
    return cookie ? JSON.parse(decodeURIComponent(cookie.slice(SESSION_COOKIE_NAME.length + 1))) : null;
  } catch {
    return null;
  }
};

const writeSessionCookie = (value: string, maxAgeSeconds: number) => {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${SESSION_COOKIE_NAME}=${value}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch {
    // Cookies blocked: nothing saved.
  }
};

const forgetSavedSession = () => {
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // nothing saved
  }
  writeSessionCookie('', 0);
};

const saveSession = (session: LinkOutSession) => {
  const saved = JSON.stringify({session, savedAt: Date.now()});
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, saved);
  } catch {
    // Revisits in a new tab fall back to the cookie.
  }
  const value = encodeURIComponent(saved);
  if (value.length <= MAX_COOKIE_VALUE_LENGTH) {
    writeSessionCookie(value, LINK_OUT_SESSION_KEEP_MS / 1000);
  } else {
    // Still drop an earlier session's cookie, so that one can't come back.
    writeSessionCookie('', 0);
  }
};

/** This load recovered the session from the cookie alone, for `takeSessionRestoredFromCookie`. */
let restoredFromCookie = false;

/**
 * True once, on the first call after this load recovered the session from the cookie (its sessionStorage and
 * localStorage copies were gone), so `Link Out Session Restored` can say whether the cookie copy is earning its keep.
 */
export const takeSessionRestoredFromCookie = (): boolean => {
  const restored = restoredFromCookie;
  restoredFromCookie = false;
  return restored;
};

const readSavedSession = (): LinkOutSession | null => {
  let saved: SavedSession | null = null;
  try {
    saved = JSON.parse(window.localStorage.getItem(SESSION_STORAGE_KEY) || 'null');
  } catch {
    // Storage blocked or corrupt: try the cookie.
  }
  const fromCookie = !saved?.session;
  if (fromCookie) {
    saved = readSessionCookie();
  }
  if (saved?.session && saved.savedAt && Date.now() - saved.savedAt < LINK_OUT_SESSION_KEEP_MS) {
    restoredFromCookie = restoredFromCookie || fromCookie;
    return saved.session;
  }
  forgetSavedSession();
  return null;
};

const readStoredSession = (): LinkOutSession | null => {
  try {
    const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as LinkOutSession;
    }
  } catch {
    // Storage blocked or corrupt: fall through to memory.
  }
  if (window.__riseLinkOutSession) {
    return window.__riseLinkOutSession;
  }
  const saved = readSavedSession();
  if (saved) {
    // Adopt it in this tab, so another tab replacing or clearing the saved copy can't change this checkout.
    storeSession(saved);
  }
  return saved;
};

/** An identity the URL itself carries (a legacy app link, a marketing email link), which outranks the session. */
const urlIdentity = (): string | null => {
  const params = getSearchParams();
  return params.get('token') || params.get('user_id') || params.get('email');
};

/**
 * This tab's link-out session, unless the URL names someone else: a legacy link for another person must not pick up
 * the session's identity or display params.
 */
export const getLinkOutSession = (): LinkOutSession | null => {
  const session = readStoredSession();
  if (!session) {
    return null;
  }
  const fromUrl = urlIdentity();
  if (fromUrl && fromUrl !== session.personId) {
    return null;
  }
  return session;
};

/**
 * Whether this page belongs to a visit the Rise app opened: its link-out token was redeemed, so the person has the app
 * and came from it. A legacy `?token=` link or a marketing email doesn't count, since nothing says the app is there.
 */
export const isLinkOutVisit = (): boolean => getLinkOutSession() !== null;

const clearSession = () => {
  delete window.__riseLinkOutSession;
  restoredFromCookie = false;
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // nothing stored
  }
  forgetSavedSession();
};

const storeSession = (session: LinkOutSession) => {
  window.__riseLinkOutSession = session;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Memory copy above still serves this page load.
  }
  if (!hasPurchased()) {
    saveSession(session);
  }
};

/** Set once this tab completes a purchase. Same key as the inline script in index.html. Change both or neither. */
export const PURCHASED_STORAGE_KEY = 'rise.linkout.purchased';

const hasPurchased = (): boolean => {
  if (window.__riseLinkOutPurchased) {
    return true;
  }
  try {
    return window.sessionStorage.getItem(PURCHASED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Call when a purchase completes. Drops the 24-hour device copies of the identity (the scrubbed queries and the
 * session, in localStorage and the cookie) and marks the tab, so neither this code nor index.html's Back handler saves
 * them again, and someone else on this device can't pick them up. The tab's own copies (history.state, sessionStorage) stay, so the confirmation and
 * success pages still know who bought.
 */
export const forgetSavedIdentityAfterPurchase = () => {
  // Kept in memory too: with sessionStorage blocked, the cookie would otherwise be saved again by the next page.
  window.__riseLinkOutPurchased = true;
  try {
    window.sessionStorage.setItem(PURCHASED_STORAGE_KEY, '1');
  } catch {
    // Storage blocked: the memory copy above still stops this page saving the identity again.
  }
  try {
    const saved: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key === SESSION_STORAGE_KEY || key?.startsWith(`${QUERY_STATE_KEY}:`)) {
        saved.push(key);
      }
    }
    saved.forEach(key => window.localStorage.removeItem(key));
  } catch {
    // Storage blocked: nothing saved there.
  }
  writeSessionCookie('', 0);
};

/** The gate shows nothing while redeeming, so a stalled request must give up and render the page without an identity. */
export const REDEEM_TIMEOUT_MS = 8000;
/** A dropped connection fails fast, so it's worth one more try once a reconnecting network has had a moment. */
export const REDEEM_NETWORK_RETRY_DELAY_MS = 1000;

const navigationType = (): string | undefined => {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (entry) {
      return entry.type;
    }
    return ({1: 'reload', 2: 'back_forward'} as Record<number, string>)[performance.navigation?.type];
  } catch {
    return undefined;
  }
};

const cameFromThisSite = (): boolean => {
  try {
    return document.referrer !== '' && new URL(document.referrer).origin === window.location.origin;
  } catch {
    return false;
  }
};

/**
 * What the identity stores held when this page loaded, before `forgetSessionOnFreshVisit` decided, plus what it did.
 * Attached to the missing-token event, so a load that ends on "Open Rise" says why it had no identity. Presence
 * flags, ages and the navigation type only: never an id, a token or a query.
 */
export type LinkOutLoadDiagnostics = {
  navigationType: string;
  cameFromThisSite: boolean;
  /** This tab's session (sessionStorage or memory), adopted from the saved copy or not. */
  sessionInTab: boolean;
  /** The 24-hour localStorage copy, expired or not. */
  savedSession: boolean;
  /** The 24-hour cookie copy, expired or not. */
  savedCookie: boolean;
  /** How many keys this site had in localStorage: 0 says the browser emptied it, not that our code removed our keys. */
  localStorageKeys: number | null;
  /** Where Segment's anonymous id survived (it keeps one copy in each), for comparison with our two copies. */
  segmentIdInLocalStorage: boolean;
  segmentIdInCookie: boolean;
  savedSessionAgeMin: number | null;
  savedSessionExpired: boolean;
  /** This path is in the session's list of pages the visit showed. */
  pathInSession: boolean;
  /** `forgetSessionOnFreshVisit` treated the load as a new visit and cleared a session (in the tab or saved). */
  clearedAsFreshVisit: boolean;
  purchasedMarker: boolean;
  /** This load's URL carried a link-out token (?h=). */
  linkOutTokenArrived: boolean;
  /** The query scrub's copies of this path's query (for ?token= links): history entry, tab, saved. */
  queryInHistoryState: boolean;
  queryInTab: boolean;
  querySaved: boolean;
};

let loadDiagnostics: LinkOutLoadDiagnostics | null = null;

export const getLinkOutLoadDiagnostics = (): LinkOutLoadDiagnostics | null => loadDiagnostics;

const readJson = (storage: Storage, key: string): any => {
  try {
    return JSON.parse(storage.getItem(key) || 'null');
  } catch {
    return null;
  }
};

const has = (read: () => unknown): boolean => {
  try {
    return Boolean(read());
  } catch {
    return false;
  }
};

const takeLoadSnapshot = (type: string | undefined): Omit<LinkOutLoadDiagnostics, 'clearedAsFreshVisit'> => {
  const path = window.location.pathname;
  const inTab = (() => {
    try {
      return readJson(window.sessionStorage, SESSION_STORAGE_KEY) ?? window.__riseLinkOutSession ?? null;
    } catch {
      return window.__riseLinkOutSession ?? null;
    }
  })() as LinkOutSession | null;
  const saved = (() => {
    try {
      return readJson(window.localStorage, SESSION_STORAGE_KEY);
    } catch {
      return null;
    }
  })() as {session?: LinkOutSession; savedAt?: number} | null;
  const cookie = readSessionCookie();
  // The localStorage copy's age when there is one, else the cookie's.
  const savedAt = saved?.session ? saved.savedAt : cookie?.savedAt;
  const savedAgeMs = savedAt ? Date.now() - savedAt : null;
  return {
    navigationType: type ?? 'unknown',
    cameFromThisSite: cameFromThisSite(),
    sessionInTab: Boolean(inTab),
    savedSession: Boolean(saved?.session),
    savedCookie: Boolean(cookie?.session),
    localStorageKeys: (() => {
      try {
        return window.localStorage.length;
      } catch {
        return null;
      }
    })(),
    segmentIdInLocalStorage: has(() => window.localStorage.getItem('ajs_anonymous_id')),
    segmentIdInCookie: has(() => document.cookie.split('; ').some(c => c.startsWith('ajs_anonymous_id='))),
    savedSessionAgeMin: savedAgeMs === null ? null : Math.round(savedAgeMs / 60000),
    savedSessionExpired: savedAgeMs !== null && savedAgeMs >= LINK_OUT_SESSION_KEEP_MS,
    pathInSession: Boolean((inTab ?? saved?.session ?? cookie?.session)?.paths?.includes(path)),
    purchasedMarker: has(() => window.sessionStorage.getItem(PURCHASED_STORAGE_KEY)),
    linkOutTokenArrived: Boolean(window.__riseLinkOutArrived),
    queryInHistoryState: has(() => window.history.state?.[QUERY_STATE_KEY]),
    queryInTab: has(() => window.sessionStorage.getItem(`${QUERY_STATE_KEY}:${path}`)),
    querySaved: has(() => window.localStorage.getItem(`${QUERY_STATE_KEY}:${path}`)),
  };
};

/**
 * Call once per page load, before reading the session. The tab keeps its session when this load continues the same
 * visit: a reload, back/forward, a full-page navigation from one of our own pages (checkout's
 * `window.location.href = '/offer/success?...'`, which leaves the identity out of the URL; netlify.toml's
 * `strict-origin` Referrer-Policy still sends the origin for those), or a page this visit already showed (iOS Safari
 * reloads an evicted tab as a plain load with no referrer, the case #330 handles for the query). Any other load (a
 * link from the app, an email, a typed URL) is a fresh visit and starts without it, so it can't check out as whoever
 * an earlier link was for. The token is single-use, so keeping the session is the only way a continuing load sees the
 * identity.
 */
export const forgetSessionOnFreshVisit = () => {
  const type = navigationType();
  const snapshot = takeLoadSnapshot(type);
  const reopensAPageOfThisVisit = readStoredSession()?.paths?.includes(window.location.pathname) ?? false;
  const freshVisit = type !== 'reload' && type !== 'back_forward' && !cameFromThisSite() && !reopensAPageOfThisVisit;
  const hadSession =
    snapshot.sessionInTab || ((snapshot.savedSession || snapshot.savedCookie) && !snapshot.savedSessionExpired);
  loadDiagnostics = {...snapshot, clearedAsFreshVisit: freshVisit && hadSession};
  if (freshVisit) {
    clearSession();
    // A token an earlier load left unredeemed (its request never got an answer) belongs to that visit too, unless
    // this load brought it.
    if (!window.__riseLinkOutArrived) {
      clearPendingRedeem();
    }
  }
};

/** Pages every checkout lands on after a successful payment: the …success pages and the trial confirmation. */
const CONFIRMATION_PATH = /success|^\/email-confirmation-trial\/?$/i;

/**
 * Backstop for checkouts that don't report their purchase through `usePaywallAnalytics`: reaching a confirmation page
 * means the payment went through.
 */
export const forgetSavedIdentityOnConfirmationPage = (pathname: string) => {
  if (CONFIRMATION_PATH.test(pathname)) {
    forgetSavedIdentityAfterPurchase();
  }
};

/** Record a page the visit reached, for `forgetSessionOnFreshVisit`. */
export const rememberLinkOutPath = (pathname: string) => {
  const session = readStoredSession();
  if (session) {
    // Saved even when the path is already recorded: each page that uses the session restarts its 24 hours.
    storeSession(
      session.paths?.includes(pathname) ? session : {...session, paths: [...(session.paths ?? []), pathname]},
    );
  }
};

export type RedeemOutcome = 'none' | 'redeemed' | 'failed';

/** What index.html's early request resolves to, and what the in-bundle request is normalised to. */
type RedeemResult = ({status: number; body: unknown} | {error: 'timeout' | 'network'}) & {
  /** How long the request itself took; index.html's early request records it when the response arrives. */
  ms?: number;
};

/** The response index.html saved for this token on an earlier load of the tab, if any. */
const takeSavedEarlyResult = (h: string): RedeemResult | null => {
  try {
    const saved = window.sessionStorage.getItem(EARLY_RESULT_STORAGE_KEY);
    window.sessionStorage.removeItem(EARLY_RESULT_STORAGE_KEY);
    const parsed = saved ? (JSON.parse(saved) as {h?: string; result?: RedeemResult}) : null;
    // Only an answer is worth reusing; a failed request is repeated instead.
    return parsed?.h === h && parsed.result && 'status' in parsed.result ? parsed.result : null;
  } catch {
    return null;
  }
};

const postRedeem = async (h: string): Promise<RedeemResult> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/api/link-out-redeem`,
      {h, n: pendingNonce()},
      {timeout: REDEEM_TIMEOUT_MS},
    );
    return {status: response.status, body: response.data};
  } catch (e) {
    if (axios.isAxiosError(e) && e.response) {
      return {status: e.response.status, body: e.response.data};
    }
    return {error: axios.isAxiosError(e) && e.code === 'ECONNABORTED' ? 'timeout' : 'network'};
  }
};

/** The request index.html already started for this token, once; the bundle sends its own when there is none. */
const takeEarlyRedeem = (): {result: Promise<RedeemResult>; startedAt: number} | null => {
  const result = window.__riseLinkOutRedeem;
  const startedAt = window.__riseLinkOutRedeemStartedAt;
  delete window.__riseLinkOutRedeem;
  delete window.__riseLinkOutRedeemStartedAt;
  return result ? {result, startedAt: startedAt ?? Date.now()} : null;
};

type RedeemResponse = {
  person_id: number;
  first_name: string | null;
  params: Record<string, string | number>;
};

/**
 * Spend the pending token, if there is one, and keep what Hypnos returns for the tab. A failed redeem (used,
 * expired, network, timeout) leaves the page without an identity, exactly as if it had been opened with no params.
 * Without an answer (network, timeout) the token stays pending, so calling this again repeats the request.
 */
export async function redeemPendingLinkOutToken(): Promise<{
  outcome: RedeemOutcome;
  reason?: string;
  /** How long the request took, from whichever of index.html or the bundle sent it. */
  requestMs?: number;
  /** Whether index.html had already started the request before the bundle loaded. */
  early?: boolean;
}> {
  const h = readPendingLinkOutToken();
  if (!h) {
    return {outcome: 'none'};
  }
  // A reopen of the link this device's saved session came from (index.html skips these before they get here).
  const savedSession = readSavedSession();
  if (savedSession?.tokenKey === linkOutTokenKey(h)) {
    clearPendingRedeem();
    storeSession(savedSession);
    return {outcome: 'none'};
  }
  // A new link replaces whatever an earlier link left in this tab, even if redeeming it fails.
  clearSession();
  let keepPending = false;
  try {
    const early = takeEarlyRedeem();
    // A reload after index.html's request spent the token: its saved response is the only copy.
    const saved = early ? null : takeSavedEarlyResult(h);
    const startedAt = early?.startedAt ?? Date.now();
    let result = early ? await early.result : saved ?? (await postRedeem(h));
    if ('error' in result && result.error === 'network') {
      await new Promise(resolve => setTimeout(resolve, REDEEM_NETWORK_RETRY_DELAY_MS));
      result = await postRedeem(h);
    }
    const timing = {requestMs: result.ms ?? Date.now() - startedAt, early: early !== null || saved !== null};
    if ('error' in result) {
      // No answer from Hypnos: a timeout, a dropped connection, or this page unloading mid-request (a reload aborts
      // it). Keep the token and nonce, so the next load of the tab, or the gate's try-again, can repeat the request
      // and get the answer.
      keepPending = true;
      storePendingToken(h);
      return {outcome: 'failed', reason: result.error, ...timing};
    }
    if (result.status !== 200 || !result.body) {
      return {
        outcome: 'failed',
        reason: (result.body as {error?: string} | null)?.error ?? `http_${result.status}`,
        ...timing,
      };
    }
    const {person_id, first_name, params} = result.body as RedeemResponse;
    storeSession({
      personId: String(person_id),
      firstName: first_name,
      params: Object.fromEntries(Object.entries(params ?? {}).map(([k, v]) => [k, String(v)])),
      paths: [window.location.pathname],
      tokenKey: linkOutTokenKey(h),
    });
    return {outcome: 'redeemed', ...timing};
  } catch {
    return {outcome: 'failed', reason: 'unknown'};
  } finally {
    if (!keepPending) {
      clearPendingRedeem();
    }
  }
}

/** A display param the link-out session carries (`firstName` included), or null. */
export const getLinkOutParam = (name: string): string | null => {
  const session = getLinkOutSession();
  if (!session) {
    return null;
  }
  if (name === 'firstName') {
    return session.firstName;
  }
  // The gift page's attribution param; the app no longer puts it in the URL.
  if (name === 'purchaser_person_id') {
    return session.personId;
  }
  return session.params[name] ?? null;
};

/**
 * `token=<value>&` for an internal URL, or '' when the value is this tab's link-out identity: that already lives in
 * the session, and writing it back into a URL would hand it to the page's analytics. Any other value (an email from a
 * marketing link, say) is returned exactly as the URL used to carry it.
 */
export const tokenParam = (token: string | null | undefined, {encode = false}: {encode?: boolean} = {}): string => {
  if (token && token === getLinkOutSession()?.personId) {
    return '';
  }
  return `token=${encode && token ? encodeURIComponent(token) : token}&`;
};
